import { m } from 'framer-motion';
// @mui
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, Card, Container, Grid, Hidden, Stack, Typography } from '@mui/material';
import Slider from 'react-slick';
// components
import { useRef } from 'react';
import Image from '../../components/Image';
import { MotionViewport, varFade } from '../../components/animate';
import { CarouselArrows } from '../../components/carousel';

// ----------------------------------------------------------------------

const CARDS = [
  {
    icon: '/assets/Icones/energy.png',
    title: "Raccordement à l'électricité",
    link: '',
    progress: 72.6,
    description: "Un accès plus facile à l'électricité par la réduction des coûts des travaux de raccordements",
    subDesc:
      'Le Togo vise à atteindre un taux de couverture de 100% en énergie au profit de toute sa population à l’horizon 2030',
  },
  {
    icon: '/assets/Icones/Money.png',
    title: 'Obtention de prêts',
    link: '',
    progress: 70,
    description: 'Un accès à l’information sur le crédit par l’élargissement de la couverture du bureau de crédit',
    subDesc: "(Bureau d'information sur le crédit et adoption de la loi sur le crédit-bail)",
  },
  {
    icon: '/assets/Icones/Building.png',
    title: "Facilitation de la création d'entreprise",
    link: '',
    progress: 95.1,
    description: 'Le Togo est le premier pays de la sous-région en termes de délai',
    subDesc: "(Un délai de création d'entreprises réduit à 4h)",
  },
  {
    icon: '/assets/Icones/576-5761790_monitoring-and-resolution-construction-worker-icon-png.png',
    title: 'Transparence dans le traitement des permis de construction',
    link: '',
    progress: 64.1,
    description: 'Une réduction des droits de demande de permis de construction',
    subDesc: '(Amélioration du contrôle de la qualité des bâtiments)',
  },
  {
    icon: '/assets/Icones/3759840.png',
    title: "Facilitation de l'enregistrement immobilier",
    link: '',
    progress: 72.6,
    description: 'Un délai de transfert de propriété réduit à 8 jours et une centralisation des procédures foncières',
    subDesc: 'Un nouveau code foncier et une réduction du coût (environ 60 USD)',
  },
];

const shadowIcon = (color) => `drop-shadow(2px 2px 2px ${alpha(color, 0.48)})`;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
  },
}));

const CardStyle = styled(Card)(({ theme }) => {
  const shadowCard = (opacity) =>
    theme.palette.mode === 'light'
      ? alpha(theme.palette.grey[500], opacity)
      : alpha(theme.palette.common.black, opacity);

  return {
    border: 0,
    maxWidth: 350,
    minWidth: 340,
    minHeight: 390,
    margin: 'auto',
    marginTop: 50,
    textAlign: 'center',
    padding: theme.spacing(5, 5, 5),
    boxShadow: theme.customShadows.z12,
    [theme.breakpoints.up('md')]: {
      boxShadow: 'none',
      backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800],
    },
    '&.cardCenter': {
      [theme.breakpoints.up('md')]: {
        paddingLeft: 50,
        paddingRight: 50,
        backgroundColor: theme.palette.background.paper,
        '&:before': {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          content: "''",
          margin: 'auto',
          position: 'absolute',
          width: 'calc(100% - 40px)',
          height: 'calc(100% - 40px)',
          borderRadius: Number(theme.shape.borderRadius) * 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `-20px 20px 40px 0 ${shadowCard(0.12)}`,
        },
      },
    },
  };
});

const HeroOverlayStyle = styled(m.img)({
  zIndex: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const ProgressContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  padding: 2,
  height: 25,
  borderRadius: 10,
  border: '2px solid #BCBFC5',
});

const ProgressTextContainer = styled(Stack)({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  alignItems: 'center',
  justifyContent: 'center',
});

const ProgressLine = styled(Box)({
  borderRadius: 10,
  height: '100%',
  background: 'transparent linear-gradient(101deg, #45bbc8 0%, #bad884 50%) 0% 0% no-repeat padding-box;',
});

// ----------------------------------------------------------------------

export default function HomeReforme() {
  const theme = useTheme();
  const slider = useRef(null);

  const settings2 = {
    dots: false,
    arrows: false,
    centerMode: false,
    swipeToSlide: true,
    focusOnSelect: true,
    variableWidth: true,
    centerPadding: '0px',
    slidesToShow: 2,
  };

  const handlePrevious = () => {
    slider.current?.slickPrev();
  };

  const handleNext = () => {
    slider.current?.slickNext();
  };

  const isLight = theme.palette.mode === 'light';

  return (
    <RootStyle>
      <Grid container>
        <Grid item xs={12} md={3}>
          <Stack position="relative" height={{ xs: '50vh', md: 'calc(100% - 60px)' }}>
            <HeroOverlayStyle
              alt="Doing Business 2022: les cinp réformes phares du Togo"
              src="/assets/background/doing_business.jpg"
            />
            <Stack
              position="absolute"
              height={300}
              bottom={{ xs: 0, md: 200 }}
              left={35}
              right={20}
              width={0.8}
              p={2}
              bgcolor={alpha('#209873A8', 0.6)}
              justifyContent="center"
            >
              <Typography variant="h3" color="common.white">
                Doing Business 2022: les cinq réformes phares du Togo
              </Typography>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} md={9} sx={{ pt: { xs: 10, sm: 10, md: 15 } }}>
          <Container component={MotionViewport}>
            <Box
              sx={{
                textAlign: 'center',
                mb: { xs: 5, md: 1 },
                px: 3,
              }}
            >
              <m.div variants={varFade().inDown}>
                <Typography variant="h2">
                  D'importantes réformes ont été opérées pour rendre le Togo plus attractif
                </Typography>
              </m.div>
            </Box>
            <Hidden mdDown>
              <Slider adaptiveHeight ref={slider} {...settings2}>
                {CARDS.map((card, index) => (
                  <Stack px={3} key={card.title}>
                    <m.div variants={varFade().inUp}>
                      <CardStyle className={'cardCenter'}>
                        <Stack height={380} justifyContent="space-between">
                          <Stack spacing={3} mb={1}>
                            <Image
                              src={card.icon}
                              alt={card.title}
                              sx={{
                                mx: 'auto',
                                width: 60,
                                height: 60,
                                filter: (theme) => shadowIcon(theme.palette.primary.main),
                                ...(index === 0 && {
                                  filter: (theme) => shadowIcon(theme.palette.info.main),
                                }),
                                ...(index === 1 && {
                                  filter: (theme) => shadowIcon(theme.palette.error.main),
                                }),
                              }}
                            />
                            <Typography variant="h5" paragraph>
                              {card.title}
                            </Typography>

                            <ProgressContainer>
                              <ProgressLine width={`${card.progress}%`} />
                              <ProgressTextContainer>
                                <Typography pr={1} fontSize={10} fontWeight="bold" color="common.white">
                                  {' '}
                                  {card.progress} sur 100{' '}
                                </Typography>
                              </ProgressTextContainer>
                            </ProgressContainer>

                            <Typography sx={{ color: isLight ? 'text.secondary' : 'common.white' }}>
                              {card.description}
                            </Typography>
                          </Stack>

                          <Typography
                            fontSize={13}
                            fontStyle="italic"
                            sx={{ color: isLight ? 'green' : 'common.white' }}
                          >
                            {card.subDesc}
                          </Typography>
                        </Stack>
                      </CardStyle>
                    </m.div>
                  </Stack>
                ))}
              </Slider>
              <Stack width={1} pb={1} direction="row" justifyContent={{ xs: 'center', md: 'end' }}>
                <CarouselArrows color="#cb011c" filled onNext={handleNext} onPrevious={handlePrevious} />
              </Stack>
            </Hidden>
            <Hidden mdUp>
              <Stack spacing={2} width={1} justifyContent="center">
                {CARDS.map((card, index) => (
                  <Stack px={{ md: 3 }} key={card.title}>
                    <m.div variants={varFade().inUp}>
                      <CardStyle className={'cardCenter'}>
                        <Stack height={380} justifyContent="space-between">
                          <Stack spacing={3} mb={1}>
                            <Image
                              src={card.icon}
                              alt={card.title}
                              sx={{
                                mx: 'auto',
                                width: 60,
                                height: 60,
                                filter: (theme) => shadowIcon(theme.palette.primary.main),
                                ...(index === 0 && {
                                  filter: (theme) => shadowIcon(theme.palette.info.main),
                                }),
                                ...(index === 1 && {
                                  filter: (theme) => shadowIcon(theme.palette.error.main),
                                }),
                              }}
                            />
                            <Typography variant="h5" paragraph>
                              {card.title}
                            </Typography>

                            <ProgressContainer>
                              <ProgressLine width={`${card.progress}%`} />
                              <ProgressTextContainer>
                                <Typography pr={1} fontSize={10} fontWeight="bold" color="common.white">
                                  {card.progress} sur 100
                                </Typography>
                              </ProgressTextContainer>
                            </ProgressContainer>

                            <Typography sx={{ color: isLight ? 'text.secondary' : 'common.white' }}>
                              {card.description}
                            </Typography>
                          </Stack>

                          <Typography
                            fontSize={13}
                            fontStyle="italic"
                            sx={{ color: isLight ? 'green' : 'common.white' }}
                          >
                            {card.subDesc}
                          </Typography>
                        </Stack>
                      </CardStyle>
                    </m.div>
                  </Stack>
                ))}
              </Stack>
            </Hidden>
          </Container>
        </Grid>
      </Grid>
    </RootStyle>
  );
}
